import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { throttle } from "throttle-debounce";
import { getAbsoluteTimeByPlayerTime, getPlayerTimeByAbsoluteTime, getStreamDay } from "../../../common/src";
import {  getStreamMeta, getStreamSessions } from "../../../common/src/stream";
import draggableElement from "../utils/draggableElement";
import { sendMessage } from "../utils/sendMessage";
import { EPISODE_STATUSES, RECORD_STATUSES } from "../constants/common";
import { getEpisodeData, getEpisodeRecordStatus, getStreamRecordStatus } from "../helpers/stream-helper";
import { getEpisodeStatus } from "../helpers/schedule-helper";
import { useWidgetContext } from "./WidgetContextProvider";
import { TYPE_BROADCAST } from "../constants/widget-types";
import { useScreenOrientation } from "../hooks/useScreenOrientation";
import { getEmbedEpisode } from "../helpers/embed-episode";

const sendCurrentTime = (currentTime, absoluteTime) => sendMessage('currentTime', { currentTime, absoluteTime });
const sendEpisode = (episode) => sendMessage('currentEpisode', episode);
const throttledSendCurrentTime = throttle(1000, sendCurrentTime);

const isTabletView = () => window.innerWidth < 900;
const isMobileView = (isOrientationLandscape) => isOrientationLandscape ? window.innerHeight < 490 : window.innerWidth < 490;

export const isPictureInPictureSupported = 'pictureInPictureEnabled' in document;

export const PlayerContext = createContext(null);

export const usePlayerContext = () => useContext(PlayerContext);

export const PlayerContextProvider = ({ children }) => {
    // const {event} = useEventContext();
    const { widgetType } = useWidgetContext();
    const { isOrientationLandscape } = useScreenOrientation();
    const { pathname, search } = useLocation();
    const params = new URLSearchParams(search);
    const miniPlayer = useRef(false);
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [stream, setStream] = useState(null);
    const [streamDay, setStreamDay] = useState(undefined);
    const [episode, setEpisode] = useState(null);
    const [recordStatus, setRecordStatus] = useState(null);
    // const [currentEpisode, setCurrentEpisode] = useState(null);
    const [duration, setDuration] = useState(null);
    const [currentTime, setCurrentTime] = useState(null);
    const [playingOptions, setPlayingOptions] = useState(null);
    const [changedBroadcastSession, setChangedBroadcastSession] = useState(null);
    const [sideModuleEnabled, setSideModuleEnabled] = useState(false);
    const [currentModule, setCurrentModule] = useState(params.get('module-active') || null);
    const [isFloating, setFloating] = useState(false);
    const [fullScreen, setFullScreen] = useState(null); // FULLSCREEN_MODES в качестве стейта
    const [tabletView, setTabletView] = useState(isTabletView());
    const [mobileView, setMobileView] = useState(isMobileView(isOrientationLandscape));
    const [isShowPlayerHalls, setIsShowPlayerHalls] = useState(false);
    const [showCountdown, setShowCountdown] = useState(null);
    const isNotMountMiniPlayer = useRef(false);
    const pageWasPlayerUnpinned = useRef(null);
    const [paused, setPaused] = useState(true);
    const containerRef = useRef(null);

    const eventStreams = event?.stream || [];
    const [, , watchKey] = pathname.split('/');
    const embedEpisodeKey = watchKey?.startsWith('r') && watchKey;

    const { episode: embedEpisode, isEpisodeRecord: isEmbedRecord, stream: embedStream, offset: embedEpisodeOffset, playable: embedPlayable } = getEmbedEpisode(embedEpisodeKey, eventStreams);

    const sessions = useMemo(() => getStreamSessions(stream), [stream]);
    const currentTimeUnix = stream && getAbsoluteTimeByPlayerTime(currentTime, streamDay, sessions, stream.timeStart, stream.timeStop);
    const timeOffset = (stream && recordStatus === RECORD_STATUSES.RECORD_CUT && stream.offset) || 0;

    const currentEpisode = embedEpisode ||
        (stream?.episode || []).find(({ timeStart, timeStop }) =>
            (timeStart - timeOffset) < currentTimeUnix && (timeStop - timeOffset) > currentTimeUnix);

    useEffect(() => {
        const messageHandler = (event) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                if (data?.hasOwnProperty('setCurrentTime')) {
                    setCurrentTime(data.setCurrentTime);
                }

                if (data?.action === 'getCurrentEpisode') {
                    const episode = getEpisodeData(currentEpisode);
                    window.parent.postMessage(JSON.stringify({ currentEpisode: episode }), '*');
                }
            } catch (error) {
                console.error(error);
            }
        };

        window.addEventListener('message', messageHandler);

        return () => {
            window.removeEventListener('message', messageHandler);
        };
    }, [currentEpisode]);

    useEffect(() => {
        if (embedEpisode) {

            const _options = {
                stream: embedStream,
                episode: null,
                streamDay: getStreamDay(embedStream.timeStart, embedEpisode.timeStart),
            };

            if (isEmbedRecord) {
                _options.episode = embedEpisode;
            } else {
                const day = !['done', 'file'].includes(String(embedStream.archiveStatus).toLowerCase())
                    ? getStreamDay(embedStream.timeStart, embedEpisode.timeStart)
                    : undefined;
                _options.streamDay = day;
            }

            setStream(embedStream);
            setPlayingOptions(_options);

        }
    }, [embedEpisode]);

    useEffect(() => {
        let _currentEpisode = null;
        if (currentEpisode) {
            _currentEpisode = getEpisodeData(currentEpisode);
        }
        sendEpisode(_currentEpisode);
    }, [currentEpisode]);

    useEffect(() => {
        throttledSendCurrentTime(currentTime, currentTimeUnix);
    }, [currentTimeUnix]);

    useEffect(() => {
        const fullScreenListener = () => {
            if (!document.fullscreenElement) {
                setFullScreen(null);
            }
        };
        const resizeListener = () => {
            setMobileView(isMobileView(isOrientationLandscape));
            setTabletView(isTabletView());
        }

        window.addEventListener('resize', resizeListener);
        document.addEventListener('fullscreenchange', fullScreenListener);

        resizeListener();

        return () => {
            document.removeEventListener('fullscreenchange', fullScreenListener);
            window.removeEventListener('resize', resizeListener);
        }
    }, [isOrientationLandscape]);

    useEffect(() => {
        const playerElement = document.querySelector('#player-container');
        const video = playerElement?.querySelector('video');
        if (!playerElement || !video) {
            return;
        }

        if (isFloating) {
            playerElement.classList.add('player-container-hidden');
            if (!video.paused) {
                video.addEventListener('leavepictureinpicture', () => {
                    navigate(`/event/${watchKey}${pageWasPlayerUnpinned.current ? '/' + pageWasPlayerUnpinned.current : ''}`);
                    mountPlayer();
                });
                if (isPictureInPictureSupported) {
                    video.requestPictureInPicture()
                        .catch((error) => {
                            console.error('error to open PIP', error);
                            playerElement.classList.remove('player-container-hidden');
                            draggableElement(playerElement);
                        });
                } else {
                    playerElement.classList.remove('player-container-hidden');
                    draggableElement(playerElement);
                }
            }
        } else {
            if (document.pictureInPictureElement) {
                document.exitPictureInPicture();
            }
        }
    }, [isFloating]);

    const mountPlayer = (mode) => {
        if (miniPlayer.current && mode === "miniplayer") return; // без этого на андроиде в Яндекс браузере при уходе со страницы трансляции при уже открепленном плеере совершается принудительный редирект обратно на страницу трансляции

        const playerElement = document.querySelector('#player-container');

        if (!playerElement) return;

        const node = document.querySelector(mode === "miniplayer" ? '#root' : '#player-block-container');
        node?.appendChild(playerElement);
        miniPlayer.current = mode === "miniplayer";
        setFloating(mode === "miniplayer");

        if (mode === "miniplayer") {
            containerRef.current = playerElement;
            const [, , , pageName] = pathname.split('/');
            pageWasPlayerUnpinned.current = pageName;
        }
    }

    const unmountPlayer = () => {
        const playerParentElement = document.querySelector('#root');
        const playerElement = document.querySelector('#player-container');
        if (!playerParentElement || !playerElement || isFloating) return;
        playerParentElement.appendChild(playerElement);
        setStream(null);
    }

    const clearPlayer = () => {
        const playerElement = document.querySelector('#player-container');
        const video = playerElement?.querySelector('video');
        video?.pause();
        if (document.pictureInPictureElement) {
            document.exitPictureInPicture();
        }

        setFloating(false);
        setStream(null);
    }

    const context = {
        event,
        setEvent,
        embedEpisodeKey,
        isPlayerWidget: widgetType === TYPE_BROADCAST,
        stream,
        streamDay,
        setStreamDay,
        streamPlayable: embedEpisode ? embedPlayable : !!stream,
        episode,
        currentEpisode: episode || currentEpisode,
        currentTime,
        duration,
        onCurrentTimeChange: setCurrentTime,
        onDurationChange: setDuration,
        setStream(_stream = null, _episode = null) {
            const _streamRecordStatus = getStreamRecordStatus(_stream);
            const _episodeRecordStatus = getEpisodeRecordStatus(_episode);

            const { isLive, isRecord, isFlussonic } = getStreamMeta(_stream);
            const sessions = getStreamSessions(_stream);

            let options = {
                stream: _stream,
                episode: null,
                streamDay: !isRecord ? (isLive ? _stream?.stream?.length - 1 : 0) : undefined,
            };
            if (isFlussonic && isLive) {
                options.streamDay = getStreamDay(_stream.timeStart, Date.now() / 1000);
            }

            if (_stream) {
                const timeOffset = (_episodeRecordStatus === RECORD_STATUSES.EPISODES_CUT && _stream.offset) ? _stream.offset : 0; // высчитываем смещение, если запись порезана
                const episodeStatus = _episode ? getEpisodeStatus(_episode) : null;
                if (_episode && !_episode.isDelay && getStreamRecordStatus(_stream) === RECORD_STATUSES.EPISODES_CUT) {
                    options.episode = _episode; // если эпизод нарезан, ставим его в качестве источника видео
                } else if (_episode) {
                    const day = !isRecord ? getStreamDay(_stream.timeStart, _episode.timeStart) : undefined;
                    const startPosition = (isLive && episodeStatus === EPISODE_STATUSES.live) ? -1
                        : getPlayerTimeByAbsoluteTime(_episode.timeStart, sessions, day, _stream.timeStart) - timeOffset;

                    options.startPosition = startPosition; // делаем навигацию по эпизоду
                    options.streamDay = day;
                } else if (_stream.id !== stream?.id) {
                    options.startPosition = isLive ? -1 : 0; // возвращаем видео на начало если зал изменился
                }
            }

            if (options.streamDay < 0) {
                options.streamDay = 0;
            }

            setStream(options.stream);
            setEpisode(options.episode);
            setStreamDay(options.streamDay);
            setPlayingOptions(options);
            setRecordStatus(_streamRecordStatus);
        },
        playingOptions,
        setPlayingOptions,
        onPlaying(stream, episode) {
            setStream(stream);
            setEpisode(episode);
        },
        changedBroadcastSession,
        setChangedBroadcastSession,
        sideModuleEnabled,
        setSideModuleEnabled,
        currentModule,
        setCurrentModule,
        isFloating,
        mountPlayer,
        unmountPlayer,
        clearPlayer,
        fullScreen,
        setFullScreen,
        tabletView,
        mobileView,
        isShowPlayerHalls,
        setIsShowPlayerHalls: (status) => {
            isNotMountMiniPlayer.current = !status;
            setIsShowPlayerHalls(status);
        },
        isNotMountMiniPlayer,
        pageWasPlayerUnpinned,
        sendMessage,
        showCountdown,
        setShowCountdown,
        paused,
        setPaused,
        containerRef,
        embedEpisode,
        embedEpisodeOffset,
    };

    return (
        <PlayerContext.Provider value={context}>
            {children}
        </PlayerContext.Provider>
    );
}
